import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertTriangle, Loader2, Plus, RefreshCw, Search, SquareKanban } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DueDateLabel, PriorityBadge, RecurrenceIcon, TimeLabel } from '@components/activity/ActivityBits';
import { usePermissions } from '@hooks/usePermissions';
import { useActivityModal } from '@contexts/ActivityModalContext';
import boardService from '@services/boardService';
import activityService from '@services/activityService';
import type { Activity, Priority } from '@/types/activity';
import type { Board, BoardColumn } from '@/types/board';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { colorClasses } from '@/utils/colors';
import { PRIORITIES, PRIORITY_LABELS } from '@/utils/priority';
import { formatDateBR } from '@/utils/formatters';

/**
 * Kanban de um quadro. Drag & drop nativo do HTML5: a UI move o card na hora (otimista) e
 * chama PATCH /activities/:id/move; se o backend recusar, recarrega o quadro.
 * Mover para uma coluna "concluído" conclui a atividade; tirar de lá reabre.
 */
export const KanbanView: React.FC = () => {
  const { boardId: boardIdParam } = useParams<{ boardId?: string }>();
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('agenda.activities.write').hasPermission;
  const { openActivity, openNewActivity, version } = useActivityModal();

  const [boards, setBoards] = useState<Board[]>([]);
  const [board, setBoard] = useState<Board | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [priority, setPriority] = useState<Priority | ''>('');

  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<number | null>(null);

  useEffect(() => {
    boardService
      .getAll()
      .then(setBoards)
      .catch(setError)
      .finally(() => setLoadingBoards(false));
  }, []);

  // Sem quadro na URL → o principal (is_main), senão o primeiro.
  const selectedBoardId = useMemo(() => {
    if (boardIdParam) return Number(boardIdParam);
    const main = boards.find((b) => b.is_main) ?? boards.find((b) => b.board_type === 'daily') ?? boards[0];
    return main?.id;
  }, [boardIdParam, boards]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!selectedBoardId) return;
    setLoading(true);
    try {
      const [b, acts] = await Promise.all([
        boardService.getById(selectedBoardId),
        boardService.getActivities(selectedBoardId, {
          search: debouncedSearch || undefined,
          priority: priority || undefined,
        }),
      ]);
      setBoard(b);
      setActivities(acts);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedBoardId, debouncedSearch, priority]);

  useEffect(() => {
    load();
  }, [load, version]);

  const columns: BoardColumn[] = useMemo(() => board?.columns ?? [], [board]);

  const byColumn = useMemo(() => {
    const map: Record<number, Activity[]> = {};
    for (const c of columns) map[c.id] = [];
    for (const a of activities) {
      (map[a.board_column_id] ??= []).push(a);
    }
    for (const list of Object.values(map)) list.sort((x, y) => x.sort_order - y.sort_order || x.id - y.id);
    return map;
  }, [activities, columns]);

  // ================================ drag & drop

  const handleDragStart = (e: React.DragEvent, activity: Activity) => {
    setDraggedId(activity.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(activity.id));
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: number) => {
    if (draggedId === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== columnId) setDragOverColumn(columnId);
  };

  const handleDrop = async (e: React.DragEvent, column: BoardColumn) => {
    e.preventDefault();
    setDragOverColumn(null);
    const dragged = activities.find((a) => a.id === draggedId);
    setDraggedId(null);
    if (!dragged || dragged.board_column_id === column.id) return;

    const sortOrder = (byColumn[column.id] ?? []).length;
    // Atualização otimista — atualiza a UI primeiro.
    setActivities((prev) =>
      prev.map((a) =>
        a.id === dragged.id
          ? {
              ...a,
              board_column_id: column.id,
              column_name: column.name,
              is_done: column.is_done_column,
              is_overdue: column.is_done_column ? false : a.is_overdue,
              sort_order: sortOrder,
            }
          : a,
      ),
    );

    try {
      const updated = await activityService.move(dragged.id, column.id, sortOrder);
      if (updated) setActivities((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
    } catch (err) {
      setError(err);
      await load(); // reverte para o estado do servidor
    }
  };

  // ================================ render

  if (loadingBoards) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 size={20} className="animate-spin mr-2" /> Carregando quadros…
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <SquareKanban size={48} className="text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700">Nenhum quadro disponível</h3>
        <p className="text-slate-500 mt-1">Crie um quadro em Agenda → Quadros.</p>
      </div>
    );
  }

  const dailyBoards = boards.filter((b) => b.board_type === 'daily');
  const projectBoards = boards.filter((b) => b.board_type === 'project');

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      {/* Barra de quadro + filtros */}
      <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-end gap-3">
        <div className="w-full sm:w-56">
          <label className="text-label block mb-1">Quadro</label>
          <select
            className="select-field"
            value={selectedBoardId ?? ''}
            onChange={(e) => navigate(`/kanban/${e.target.value}`)}
          >
            {dailyBoards.length > 0 && (
              <optgroup label={BOARD_TYPE_LABELS.daily}>
                {dailyBoards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {b.is_main ? ' (principal)' : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {projectBoards.length > 0 && (
              <optgroup label="Projetos">
                {projectBoards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <label className="text-label block mb-1">Buscar</label>
          <Search size={16} className="absolute left-3 bottom-2.5 text-slate-400" />
          <input
            className="input-field pl-9"
            placeholder="Título ou descrição…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-40">
          <label className="text-label block mb-1">Prioridade</label>
          <select className="select-field" value={priority} onChange={(e) => setPriority(e.target.value as Priority | '')}>
            <option value="">Todas</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </div>

        <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} disabled={loading} className="mb-0.5">
          Atualizar
        </Button>
      </div>

      {board && (board.description || board.start_date || board.end_date) && (
        <div className="text-sm text-slate-500 flex flex-wrap gap-x-4">
          {board.description && <span>{board.description}</span>}
          {(board.start_date || board.end_date) && (
            <span>
              Período: {board.start_date ? formatDateBR(board.start_date) : '…'} a{' '}
              {board.end_date ? formatDateBR(board.end_date) : '…'}
            </span>
          )}
        </div>
      )}

      {/* Colunas */}
      <div className="flex-1 min-h-0 overflow-x-auto pb-2 snap-x sm:snap-none">
        <div className="flex gap-4 h-full min-h-[400px]">
          {loading && !board && (
            <div className="flex items-center text-slate-400">
              <Loader2 size={20} className="animate-spin mr-2" /> Carregando…
            </div>
          )}
          {columns.map((column) => {
            const cc = colorClasses(column.color);
            const items = byColumn[column.id] ?? [];
            const openCount = items.filter((a) => !a.is_done).length;
            const wipExceeded = !!column.wip_limit && !column.is_done_column && openCount > column.wip_limit;
            return (
              <div
                key={column.id}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={() => setDragOverColumn((c) => (c === column.id ? null : c))}
                onDrop={(e) => handleDrop(e, column)}
                className={clsx(
                  'w-[85vw] sm:w-72 snap-start flex-shrink-0 flex flex-col rounded-xl bg-slate-100 border-t-4 transition-colors',
                  cc.borderTop,
                  dragOverColumn === column.id && 'ring-2 ring-emerald-400 bg-emerald-50',
                )}
              >
                <div className={clsx('px-3 py-2 rounded-t-lg flex items-center justify-between', cc.soft)}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={clsx('font-semibold text-sm truncate', cc.text)}>{column.name}</span>
                    <span className="text-xs bg-white/70 text-slate-600 rounded-full px-2 py-0.5">
                      {items.length}
                      {column.wip_limit ? ` / ${column.wip_limit}` : ''}
                    </span>
                  </div>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => openNewActivity({ board_id: board?.id, board_column_id: column.id })}
                      className="p-1 rounded text-slate-500 hover:text-slate-800 hover:bg-white/60"
                      title="Nova atividade nesta coluna"
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
                {wipExceeded && (
                  <div className="mx-2 mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> Limite WIP excedido ({openCount}/{column.wip_limit})
                  </div>
                )}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
                  {items.map((a) => (
                    <div
                      key={a.id}
                      draggable={canWrite}
                      onDragStart={(e) => handleDragStart(e, a)}
                      onDragEnd={handleDragEnd}
                      onClick={() => openActivity(a.id)}
                      className={clsx(
                        'bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-card-hover transition-shadow',
                        canWrite ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
                        a.is_overdue && !a.is_done && 'border-l-4 border-l-red-500',
                      )}
                    >
                      <p className={clsx('text-sm font-medium text-slate-800', a.is_done && 'line-through text-slate-400')}>
                        {a.title}
                      </p>
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <div className="flex items-center gap-2">
                          <PriorityBadge priority={a.priority} />
                          <RecurrenceIcon activity={a} />
                        </div>
                        <div className="flex items-center gap-2">
                          <TimeLabel activity={a} />
                          <DueDateLabel activity={a} short />
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-6">
                      {canWrite ? 'Arraste atividades para cá' : 'Vazio'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
